// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import { CustomerConfig } from "utils/ConfigTypes";
import EndTestScheduleDetails from "./CustomComponents/intl/EndTestScheduleDetails";
import EndTestSuccessForProfile from "./CustomComponents/intl/EndTestSuccessForProfile";
import SwabNosePanel from "components/TestRun/steps/SwabNosePanel";
import WriteDOBForProfileString from "./CustomComponents/intl/WriteDOBForProfileString";
import { fbt } from "fbt";

export const HealthPulseConfig: CustomerConfig = {
  id: "healthpulse",
  name: "HealthPulse@home",
  pathPrefix: "healthpulse",
  allowGuestRuns: true,
  landingPage: "NEWTESTRUN",
  headerConfig: {
    headerLogo: {
      label: "HealthPulse@home",
      position: "left",
      color: "#130026",
      route: "NEWTESTRUN",
      disabledInTest: true,
    },
    headerBug: "HealthPulse_bug.svg",
    sideMenuIcon: "bars",
    headerItems: [
      {
        title: "About HealthPulse@home",
        shortTitle: "About",
        type: "dialog",
        content: [
          {
            type: "MarkDown",
            props: {
              source:
                "HealthPulse@home COVID-19 Specimen Collection Kit™ enables individuals to find out if they have COVID-19 without ever having to leave their home. [HealthPulse](https://healthpulsenow.org/about-us) solutions are developed and actively managed by [Audere](https://auderenow.org/), a Seattle-based digital health nonprofit developing software to improve global health in the world’s most underserved communities.",
            },
          },
        ],
      },
      {
        title: "Contact Support",
        shortTitle: "Contact",
        type: "link",
        href: "https://healthpulsenow.org/contact",
      },
    ],
  },
  customThemeOverride: {
    palette: {
      primary: {
        main: "#2c6ba3",
        light: "#6399d5",
        dark: "#004174",
      },
      secondary: {
        main: "#fafafa",
        light: "#fff",
        dark: "#c7c7c7",
      },
      background: {
        default: "#fff",
      },
    },
    overrides: {
      MuiAppBar: {
        colorPrimary: {
          backgroundColor: "#fff",
          color: "#6c6c70",
        },
      },
      MuiCssBaseline: {
        "@global": {
          html: { backgroundColor: "white" },
          a: {
            color: "#2c6ba3",
            "&:hover": { color: "#004174" },
          },
          strong: {
            fontWeight: 500,
          },
        },
      },
      MuiFormLabel: {
        root: {
          color: "#212b32",
          fontWeight: "normal",
        },
      },

      MuiDrawer: {
        paper: {
          backgroundColor: "#666",
          color: "#fff",
          "& .MuiIconButton-root": {
            color: "#fff",
          },
          "& .MuiDivider-root": {
            backgroundColor: "#fff",
          },
          "& .MuiListItem-button": {
            fontWeight: "300",
            fontSize: "24px",
            "&:hover": {
              textDecoration: "none",
              color: "#fff",
            },
          },
        },
      },
    },
    props: {
      MuiAppBar: {
        elevation: 1,
      },
      MuiButton: {
        color: "secondary",
      },
    },
  },
  loginImageUrl: "HealthPulseLogo.svg",
  loginHeader:
    "HealthPulse@Home enables you to order and conduct a COVID-19 test from the comfort of your home.",
  loginDescription:
    "HealthPulse@Home is offered by Audere, and enables you to order and conduct a COVID-19 antibody test from the comfort of your home.",
  customRouteTitle: {
    PERFORMTEST: "HealthPulse@home Specimen Collection Kit Instructions",
  },

  getTestRunDefinition: () => ({
    testkitIntro: {
      contentDescription: [
        {
          type: "DemoAsset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: fbt(
              "Collection kit image",
              "Healthpulse intro: image description"
            ),
            src: "healthpulse/start.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "HealthPulse@home COVID-19 Specimen Collection Kit",
              "Healthpulse intro: title"
            ),
          },
          display: "centered",
        },
        {
          type: "NextStepButton",
          props: { text: fbt("Start", "Healthpulse intro: continue button") },
          display: "centered",
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              `The collection process should take less than 10 minutes.`,
              "Healthpulse intro: note"
            ),
          },
          display: "centered",
        },
      ],
      nav: {
        next: {
          default: "gettingStarted",
        },
      },
    },
    gettingStarted: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: fbt(
              "Image of pen, lamp, and kit",
              "Healthpulse getting started: image description"
            ),
            src: "healthpulse/getstarted.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "Let's get started",
              "Healthpulse getting started: title"
            ),
          },
        },
        {
          type: "TestRunWarningDialog",
          props: {
            children: [
              "Seek immediate medical care from a healthcare professional if seriously ill. Call 9\u20111\u20111 if having a medical emergency.",
              "Signs of a serious medical condition include, but are not limited to: severe shortness of breath or difficulty breathing, coughing up blood, chest pain, irregular heartbeat, persistent vomiting or diarrhea.",
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `We will guide you through completing a nasal swab collection. Before getting started, please **have a pen handy** and place the kit on a clean, dry surface. 
                `,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `It is very **important** that you:`,
              },
            },
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source:
                    "Complete the steps as **carefully** as possible to ensure the specimen can be processed in the lab.",
                },
              },
              {
                type: "MarkDown",
                props: {
                  source:
                    "Ship the specimen to the lab **immediately** after collection (within 24 hours). If the specimen arrives at the lab too late, it will **not be processed**.",
                },
              },
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `[About the lab test](~aboutResultsFromStarting)`,
              },
            },
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "enterBarcode",
          aboutResultsFromStarting: "aboutResults",
        },
        previous: "testkitIntro",
      },
    },
    aboutResults: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "What will the COVID-19 test results tell me?",
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "Test results will indicate whether or not the person tested likely has COVID-19.",
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `If the results are **positive**, the virus was detected in the specimen. If positive, it is important to limit exposure to other people and continue to monitor symptoms.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `If the results are **negative**, the virus was not detected in the specimen. However, it is important to note that the virus may not be detected by the test in early stages of infection. Furthermore, the virus may not be detected if the specimen is not collected as directed. If there is reason to believe the person tested has been exposed, please follow up with a healthcare provider for further evaluation or to get retested.*`,
                },
              },
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "When test results are ready, an email will be sent. There will be an opportunity to speak with a licensed physician, who can answer any questions about the test results and help determine next steps in care.",
          },
        },
        {
          type: "Paragraph",
          props: {
            content:
              "*Although the possibility is low, a false negative result is possible. A false negative is when the person is actually infected with COVID-19, but the test result is negative. A false negative should be considered if the person has had recent exposure to the virus along with symptoms consistent with COVID-19. The possibility of a false negative result should especially be considered if recent exposure or symptoms suggest that COVID-19 is likely, and diagnostic tests for other respiratory illnesses are negative.",
          },
        },
        {
          type: "NextStepLink",
          props: { returnStep: true, text: "Return to test" },
        },
      ],
      nav: {
        previous: "endTest",
      },
    },
    enterBarcode: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: fbt(
              "Kit with arrows pointing to registration ID and barcode locations",
              "registration: image description"
            ),
            src: "healthpulse/registerkit.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Register kit", "registration: title"),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              `The specimen cannot be processed in the lab until it is properly registered. 
              Enter the barcode from the kit box and personal information below to register the kit.`,
              "registration: description"
            ),
          },
        },
        {
          type: "RegistrationPanel",
          props: {},
        },
        {
          type: "MarkDown",
          props: {
            source: fbt(
              "No barcode? [Contact us](https://healthpulsenow.org/contact).",
              "registration: no barcode (markdown)"
            ),
          },
        },
        {
          type: "NextStepButton",
          props: { text: fbt("Register", "registration: submit button label") },
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "checkKit",
        },
        previous: "gettingStarted",
      },
    },
    // TODO: Add TOU screen
    /* verifyEmail: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "Confirm contact information",
          },
        },
        {
          type: "Paragraph",
          props: {
            content: (
              {
              type: "MarkDown",
              props: {
                source: `When your lab results are ready, you will be contacted at the email address you used for the kit order. If you would prefer to be contacted at a different email address, enter it below.`}
              }
            ),
          },
        },
        {
          type: "EmailPanel",
          props: {},
        },
        {
          type: "NextStepButton",
          props: { text: "Confirm" },
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "checkKit",
        },
        previous: "enterBarcode",
      },
    }, */
    checkKit: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of shipping box, swab bag, tube, and specimen bag",
            src: "healthpulse/checkcomponents.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Check kit components",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `Confirm that the kit includes the items below, or [contact us](https://healthpulsenow.org/contact) 
              if an item is missing.`,
              },
            },
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              `FedEx Express Shipping bag`,
              `Swab in white paper package (do not open yet)`,
              `Plastic tube`,
              `Plastic specimen bag`,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "fedExKit",
        },
        previous: "enterBarcode",
      },
    },
    fedExKit: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of fedex kit and smartphone",
            src: "healthpulse/fedexkit.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Plan for specimen pickup/drop off",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `It's **very important** that you ship the specimen to the lab 
                  **immediately** (within 24 hours) after it is collected. If the specimen 
                  arrives at the lab too late, it will **not be processed**.`,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `Before the specimen is collected, please schedule a pickup (at no 
              additional cost) or find a drop box location:`,
          },
        },
        {
          type: "NextStepButton",
          props: { text: "Schedule pickup" },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: "[Find a dropoff location](~dropoff)",
              },
            },
          },
        },
      ],
      nav: {
        next: {
          default: "schedulePickup",
          dropoff: "findDropOff",
        },
        previous: "checkKit",
      },
    },
    schedulePickup: {
      contentDescription: [
        {
          type: "FedExShipping",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "washHands",
          callToSchedule: "callFedex",
          callToChange: "contactFedex",
        },
        previous: "fedExKit",
      },
    },

    findDropOff: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "Find a dropoff location",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `The package contains a lab specimen, which can only be dropped off at a FedEx Office, FedEx Ship Center, and FedEx Drop Box locations. **You cannot drop it off at FedEx retail locations such as Office Depot and Walgreens.**`,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `To find the right location:`,
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `Click the button below to search FedEx.com for locations in your area.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `On FedEx.com, select a location to see the **Latest Express Dropoff** hours, which may be as early as 2pm in some areas.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `Please deliver to a drop box **Sunday afternoon** to **Friday mid-day**, during hours when packages are picked up.`,
                },
              },
            ],
          },
        },
        {
          type: "NavButton",
          props: {
            text: "Locate a drop box",
            route: "http://fedex.com/labreturns",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `It is important to ship the specimen within **24 hours of collection**. Once you’ve made plans to drop off the package, please continue.`,
              },
            },
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "washHands",
        },
        previous: "fedExKit",
      },
    },

    callFedex: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "Call FedEx for pickup",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `Call [(800) GO FEDEX](tel:18004633339) to arrange a pickup from your home or office location. Place the package in the agreed-upon pickup location, out of direct sunlight.`,
              },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `When calling FedEx, the phone system will ask the questions below:`,
              },
            },
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `How can I help you? Say **"schedule a pickup"**`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `Do any packages weigh more than 150 lbs? Say **"no"**`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `To schedule a pickup, say **"Using a label"**`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `Is the word “stamp” on your label? Say **"no"**`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `When asked, say the **12-digit tracking number** on the FedEx shipping label`,
                },
              },
            ],
          },
        },
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of fedex label pointing at tracking number",
            src: "healthpulse/trackingnumber.png",
          },
          display: "centered",
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `When asked if they have the correct pickup address, say **"no"**`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `You will be transferred to a FedEx representative who will finish setting up a pickup from your address`,
                },
              },
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: { source: `[Call (800) GO FEDEX](tel:18004633339)` },
            },
          },
        },
        {
          type: "Paragraph",
          props: {
            content: "Once your pickup is scheduled, please continue. ",
          },
        },
        {
          type: "NextStepLink",
          props: {
            text: "My pickup is scheduled",
          },
        },
      ],
      nav: {
        next: {
          default: "washHands",
        },
        previous: "schedulePickup",
      },
    },

    contactFedex: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: "Contact FedEx",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `To adjust your pickup window, call FedEx at [(800) GO FEDEX](tel:18004633339).`,
              },
            },
          },
        },
      ],
      nav: {
        next: {
          default: "endTest",
        },
        previous: "schedulePickup",
      },
    },
    /*
     * TEST RUN
     */

    washHands: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of soaps",
            src: "healthpulse/washhands.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Blow nose and wash hands",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `A clear nose and clean hands are important to obtain a good specimen.`,
              },
            },
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              `Using a clean tissue, gently blow nose and remove any debris to clear both nostrils.`,
              `Anyone touching supplies must wash their hands with soap and warm water for 20 seconds and dry them thoroughly with a clean towel.`,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "openNasalSwab",
        },
        previous: "schedulePickup",
      },
    },
    openNasalSwab: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt:
              "Image of swab packaging with arrows pointing on where to pull",
            src: "healthpulse/openswab.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Open nasal swab",
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              `Remove the nasal swab from the wrapper by pulling the two 
              ends of the wrapper apart (like you would to open a band-aid).`,
              {
                type: "MarkDown",
                props: {
                  source: `Be careful to only touch the swab 
            handle, **not the tip**.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `If the swab is dropped or touches another surface, [contact us](https://healthpulsenow.org/contact).`,
                },
              },
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "swabNose",
        },
        previous: "washHands",
      },
    },
    swabNose: {
      contentDescription: [{ type: SwabNosePanel, props: {} }],
      nav: {
        next: {
          default: "swabInTube",
        },
        previous: "openNasalSwab",
      },
    },
    swabInTube: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt:
              "Image of lowering the swab into the tube and screwing the cap",
            src: "healthpulse/putswabintube.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Put swab in tube",
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: "MarkDown",
                props: {
                  source: `While **holding the swab**, open the tube.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `Put the swab **tip first** into the tube.`,
                },
              },
              {
                type: "MarkDown",
                props: {
                  source: `Screw the cap back onto the tube **tightly**.`,
                },
              },
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "writeName",
        },
        previous: "swabNose",
      },
    },
    writeName: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of tube label with date of birth and collection date",
            src: "healthpulse/filloutinfo.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Fill out info on tube",
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `The lab can **only** process the specimen if the correct 
              information is written on the tube.`,
              },
            },
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              {
                type: WriteDOBForProfileString,
                props: {},
              },
              {
                type: "MarkDown",
                props: {
                  source: `Write today's date of **${new Date().toLocaleDateString()}** on 
                the tube label under the collection date.`,
                },
              },
              `Place the tube in the specimen bag and close the top of 
                the bag securely.`,
              `Leave the white pad in the bag.`,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "washHands2",
        },
        previous: "swabInTube",
      },
    },
    washHands2: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: "Image of soaps",
            src: "healthpulse/washhands.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Wash and dry hands",
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `Before packing up the kit, thoroughly wash and dry hands.`,
          },
        },
        {
          type: "Paragraph",
          props: {
            content: `This step helps keep people who will be handling the 
            shipment safe.`,
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "prepareShipping",
        },
        previous: "writeName",
      },
    },
    prepareShipping: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt:
              "Image of placing the specimen in the box and the box in the bag",
            src: "healthpulse/prepareforshipping.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: "Prepare kit for shipping",
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              `Place the specimen bag with the tube into the original HealthPulse@home 
              kit box. The box protects the specimen during transport to the lab.`,
              `Place the paper insert with the barcode back in the box.`,
              `Slip the sleeve back over the box.`,
              `Place the kit box inside the postage-paid, pre-addressed 
                FexEx shipping bag.`,
              `Remove the clear liner at the top of the FedEx shipping bag 
                to expose the adhesive, then seal the bag closed.`,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "endTest",
        },
        previous: "washHands2",
      },
    },
    endTest: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            type: "local",
            alt: fbt(
              "Image of box, clipboard, microscope. and smartphone",
              "Healthpulse end test: image description"
            ),
            src: "healthpulse/thankyou.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Thank you", "Healthpulse end test: title"),
          },
        },
        {
          type: EndTestSuccessForProfile,
          props: {},
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "Here is what will happen next:",
              "Healthpulse end test: step detail"
            ),
          },
        },
        {
          type: EndTestScheduleDetails,
          props: {},
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              `Seek immediate medical care from a healthcare professional if seriously ill, 
                or if symptoms worsen. Call 9\u20111\u20111 if having a medical emergency.`,
              "Healthpulse end test: step detail"
            ),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              `Signs of a serious medical condition include, but are not limited to: severe 
                shortness of breath or difficulty breathing, coughing up blood, chest pain, 
                irregular heartbeat, persistent vomiting or diarrhea.`,
              "Healthpulse end test: step detail"
            ),
          },
        },
        {
          type: "Paragraph",
          props: {
            children: {
              type: "MarkDown",
              props: {
                source: `[${fbt(
                  "What will the test results say?",
                  "Healthpulse end test: link to about page"
                )}](~default)`,
              },
            },
          },
        },
        {
          type: "NavButton",
          props: {
            text: fbt("Done", "Healthpulse end test: button"),
            route: "NEWTESTRUN",
          },
        },
      ],
      nav: {
        next: {
          default: "aboutResults",
        },
        previous: "prepareShipping",
      },
    },
  }),
  showProgressBar: false,
  startStep: "testkitIntro",
  disableLogin: true,
};

export default HealthPulseConfig;
